import type { PipelineStatus } from '@@/packages/shared/types/models/connection';
import type { SupplierListReviewState } from '@@/packages/shared/types/models/connectionReview';

export default {
  en: {
    title: 'Suppliers',
    pageTitle: 'Suppliers - {0}',
    pageHeader: 'Suppliers',
    entityName: 'Supplier',
    dataTableColumns: {
      supplier: 'Supplier name',
      compliance: 'Compliance',
      pipelineStatus: 'Pipeline status',
      reviewState: 'Review status',
      reviewExpiresAt: 'Review expiry',
      criticality: 'Criticality',
      confidentiality: 'Confidentiality',
      holdsPII: 'PII',
      labels: 'Labels',
      products: 'Products',
      assignedUsers: 'Assigned',
      policiesApplied: 'Policies applied',
      dateAdded: 'Date added',
      companyNumber: 'Company number',
      country: 'Country',
      assessmentProgress: 'Assessment progress',
      lastUpdatedAt: 'Last answer updated',
      lastSignedInAt: 'Last signed in',
      lastSignedInAtTooltip:
        "The most recent sign-in by anyone at the supplier, across the whole platform rather than just your connection. It doesn't mean they've responded to you",
      sector: 'Sector',
      website: 'Website',
      exemptionsApplied: 'Exemptions applied',
      nonComplianceApplied: 'Non-compliance applied',
      remediationRequests: 'Remediation requests',
      remediationResponses: 'Remediation responses',
      evidence: 'Evidence',
      activeDiscussions: 'Active discussions',
      archivedDiscussions: 'Archived discussions',
      invitedAt: 'Date invited',
      invitedBy: 'Invite sender',
      inviteEmail: 'Invite email',
      reassessmentNextDueAt: 'Next re-assessment due',
      latestAssessmentProgressPercentage: 'Assessment progress',
      numRisks: 'Open risks',
      lastAssessmentDate: 'Last assessment',
      connectionProfileTypeResolved: 'Profile type',
      consolidatedConnectionStatus: 'Connection status',
      approvalReviewers: 'Reviewers',
    },
    dataTableColumnsTooltips: {
      lastAssessmentDateOverdue: 'Supplier re-assessment overdue',
    },
    dataTable: {
      pipelineStatus: {
        in_tender: 'In tender',
        pre_contract: 'Pre-contract',
        in_contract: 'In contract',
        other: 'Other',
      } satisfies Record<PipelineStatus, string>,
      reviewState: {
        not_reviewed: 'Not reviewed',
        in_review: 'In review',
        reviewed: 'Reviewed',
        reviewed_approved: 'Reviewed (approved)',
        reviewed_rejected: 'Reviewed (rejected)',
        needs_re_review: 'Needs re-review',
      } satisfies Record<SupplierListReviewState, string>,
      managedLabel: 'RL managed',
      managedDescription: 'This profile has been created and is managed by Risk Ledger.',
      incomplete: 'Incomplete',
      deletedUser: 'Deleted user',
      communityPeers: '{n} peer | {n} peers',
      inviteSend: 'Invite sent',
      sendRequest: 'Send request',
      sendInvite: 'Send invite',
      sendConnectionRequest: 'Request access',
      notInvited: 'Not invited',
      notConnected: 'Not connected',
      CONNECTION_STATUS_REQUEST_PENDING: 'Pending',
      CONNECTION_STATUS_CONNECTED: 'Connected',
      CONNECTION_STATUS_NOT_CONNECTED: 'Not connected',
      CONNECTION_STATUS_REQUEST_REJECTED: 'Rejected',
      CONNECTION_STATUS_REMOVED: 'Removed',
      CONNECTION_STATUS_INVITED: 'Invited',
      CONNECTION_STATUS_NOT_INVITED: 'Not invited',
    },
    filterAttributes: {
      searchPlaceholder: 'Search suppliers...',
      name: 'Name',
      companyNumber: 'Company number',
      website: 'Website',
      country: 'Country',
      product: 'Product',
      lastActiveAt: 'Last answer updated',
      lastSignedInAt: 'Last signed in',
      managedTrue: 'is managed',
      managedFalse: 'is not managed',
      percentageCompliance: 'Compliance',
      criticality: 'Criticality',
      confidentiality: 'Confidentiality',
      holdsPII: 'Holds PII',
      reviewState: 'Review state',
      pipelineStatus: 'Pipeline status',
      assignedToMeForApprovalReview: 'Reviews assigned to me',
      assignedToMeForApprovalReviewTrue: 'is assigned to me for reviews',
      assignedToMeForApprovalReviewFalse: 'is not assigned to me for reviews',
      approvalReviewers: 'Reviewers',
      rejected: 'Rejected',
      removed: 'Removed',
      reviewModifiedAt: 'Review last modified',
      reviewExpiresAt: 'Review expiry',
      numPoliciesApplied: 'Number of policies applied',
      createdAt: 'Date added',
      labels: 'Labels',
      sectorID: 'Sector',
      assignedUsers: 'Assigned users',
      numActiveDiscussions: 'Number of active discussions',
      numArchivedDiscussions: 'Number of archived discussions',
      numExemptionsApplied: 'Number of exemptions applied',
      numNonComplianceApplied: 'Number of non compliances applied',
      numRemediationRequests: 'Number of remediation requests',
      numRemediationResponses: 'Number of remediation responses',
      numEvidence: 'Number of evidence',
      assignedToMe: 'Assigned to me',
      assignedToMeTrue: 'is assigned to me',
      assignedToMeFalse: 'is not assigned to me',
      invitedAt: 'Date invited',
      invitedBy: 'Invite sender',
      inviteEmail: 'Invite email',
      reassessmentNextDueAt: 'Next re-assessment due',
      numRisks: 'Open risks',
      lastAssessmentDate: 'Last assessment date',
      assessmentProgress: 'Assessment progress',
      assessmentComplete: 'Assessment complete',
      assessmentCompleteTrue: 'True',
      assessmentCompleteFalse: 'False',
      reassessmentOverdue: 'Reassessment overdue',
      reassessmentOverdueTrue: 'True',
      reassessmentOverdueFalse: 'False',
      profileTypeResolvedClaimed: 'Not unclaimed',
      profileTypeResolvedUnclaimed: 'Unclaimed',
      connectionProfileTypeResolved: 'Profile type',
      connectionProfileTypeUnclaimed: 'Unclaimed',
      connectionProfileTypeBasic: 'Basic access',
      connectionProfileTypeFull: 'Full access',
      connectionProfileTypeManaged: 'RL managed',
      connected: 'Connected',
      notConnected: 'Not connected',
      pending: 'Pending',
      inviteSent: 'Invite sent',
      notInvited: 'Not invited',
      consolidatedConnectionStatus: 'Connection status',
    },
    viewNameRequired: 'Enter a view name',
    saveModal: {
      title: 'Save a new view',
      save: 'Save view',
      saveNew: 'Save as new view',
      saveExisting: 'Update existing view',
      updateError: 'There was an error updating the view',
      cancel: 'Cancel',
      description:
        'Give your view a name below. Once saved, it will appear in the sidebar for you and your colleagues.',
      placeholder: 'Name of view...',
    },
    editName: {
      title: "Rename view '{name}'",
      description: 'Give your view a new name below.',
      placeholder: 'Name of view...',
      cancel: 'Cancel',
      save: 'Save view',
      renameError: 'There was an error renaming the view',
    },
    deleteModal: {
      buttonAlt: 'Delete view',
      title: "Delete view '{0}'?",
      description:
        "Please confirm if you would like to remove the '{0}' view. It will no longer appear in the sidebar for you and your colleagues.",
      delete: 'Delete view',
      cancel: 'Cancel',
      failed: 'Deleting the view failed.',
    },
    placeholders: {
      emptyTitle: 'Suppliers',
      emptyDescription: 'No connections could be found',
      errorTitle: 'Suppliers',
      errorDescription: 'Failed fetching connections',
    },
    export: 'Export',
    exportError: 'There was an error attempting to export the current view',
  },
};
